import {
    PointerEvent as ReactPointerEvent,
    useRef,
    useState,
    useSyncExternalStore,
} from 'react'
import { STOREFRONT_DESKTOP_BREAKPOINT_PX } from '../../style/scrollingLayout'

const SHEET_TOP_GAP = 12
const DISMISS_DISTANCE = 90

const subscribeToViewport = (onChange: () => void) => {
    const viewport = window.visualViewport
    viewport?.addEventListener('resize', onChange)
    viewport?.addEventListener('scroll', onChange)
    window.addEventListener('resize', onChange)
    return () => {
        viewport?.removeEventListener('resize', onChange)
        viewport?.removeEventListener('scroll', onChange)
        window.removeEventListener('resize', onChange)
    }
}

const isDesktop = () => window.innerWidth >= STOREFRONT_DESKTOP_BREAKPOINT_PX

const readSheetHeight = () => {
    if (isDesktop()) return null
    const visible = window.visualViewport?.height ?? window.innerHeight
    return Math.round(visible - SHEET_TOP_GAP)
}

// The bottom edge of the visible viewport, which sits above the keyboard.
const readVisibleBottom = () => {
    if (isDesktop()) return null
    const viewport = window.visualViewport
    return viewport
        ? Math.round(viewport.offsetTop + viewport.height)
        : window.innerHeight
}

/**
 * On phones the sheet covers the bottom of the visible page. iOS doesn't
 * shrink the layout viewport for the keyboard, so the sheet is placed from
 * the visible viewport's bottom edge rather than `bottom: 0`, which keeps the
 * composer above the keyboard. Null on desktop, where the panel is full height.
 */
export function useSheetPosition(): { top: number; height: number } | null {
    const height = useSyncExternalStore(
        subscribeToViewport,
        readSheetHeight,
        () => null,
    )
    const visibleBottom = useSyncExternalStore(
        subscribeToViewport,
        readVisibleBottom,
        () => null,
    )
    if (height == null || visibleBottom == null) return null
    return { top: visibleBottom - height, height }
}

/** Drag the sheet's handle area down to close it, the way native sheets work. */
export function useSwipeToDismiss(onDismiss: () => void) {
    const [offset, setOffset] = useState(0)
    const start = useRef<number | null>(null)

    const onPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
        if (event.pointerType === 'mouse') return
        start.current = event.clientY
        event.currentTarget.setPointerCapture(event.pointerId)
    }
    const onPointerMove = (event: ReactPointerEvent<HTMLElement>) => {
        if (start.current == null) return
        setOffset(Math.max(0, event.clientY - start.current))
    }
    const onPointerUp = () => {
        if (start.current == null) return
        start.current = null
        setOffset(0)
        if (offset > DISMISS_DISTANCE) onDismiss()
    }

    return {
        offset,
        handleProps: {
            onPointerDown,
            onPointerMove,
            onPointerUp,
            onPointerCancel: onPointerUp,
        },
    }
}
